import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../config";
import { useApp } from "../context/AppContext";

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
// Fixed BEAC peg used for super-admin accounting (1 EUR = 655.957 XAF).
const EUR_TO_XAF = 655.957;

function cacheKey(isSuperAdmin) {
  return `cogency_currency_cache_${isSuperAdmin ? "super" : "user"}`;
}

function getFallback(isSuperAdmin) {
  if (isSuperAdmin) {
    return {
      currency: "XAF",
      symbol: "FCFA",
      locale: "fr-FR",
      // Subscription prices are already stored in FCFA (XOF basis), so keep
      // rate at 1. EUR-denominated costs use eurRate for conversion.
      rate: 1,
      eurRate: EUR_TO_XAF,
      country: "TD",
    };
  }
  return {
    currency: "XOF",
    symbol: "FCFA",
    locale: "fr-FR",
    rate: 1,
    eurRate: EUR_TO_XAF,
    country: "TD",
  };
}

// Map navigator.language / locale to a default currency
const LOCALE_TO_CURRENCY = {
  "fr-FR": "EUR",
  "fr-BE": "EUR",
  "fr-CH": "CHF",
  "fr-CA": "CAD",
  "fr-LU": "EUR",
  "fr-MC": "EUR",
  "fr-CM": "XAF",
  "fr-SN": "XOF",
  "fr-CI": "XOF",
  "fr-ML": "XOF",
  "fr-BF": "XOF",
  "fr-NE": "XOF",
  "fr-TG": "XOF",
  "fr-BJ": "XOF",
  "fr-GN": "GNF",
  "fr-CD": "CDF",
  "fr-CF": "XAF",
  "fr-GQ": "XAF",
  "fr-GA": "XAF",
  "fr-CG": "XAF",
  "fr-TD": "XAF",
  "fr-DZ": "DZD",
  "fr-TN": "TND",
  "fr-MA": "MAD",
  "fr-MG": "MGA",
  "fr-MU": "MUR",
  "fr-HT": "HTG",
  "fr-RW": "RWF",
  "fr-DJ": "DJF",
  "fr-KM": "KMF",
  "en-US": "USD",
  "en-GB": "GBP",
  "en-CA": "CAD",
  "en-AU": "AUD",
  "en-NZ": "NZD",
  "en-IE": "EUR",
  "en-ZA": "ZAR",
  "en-NG": "NGN",
  "en-GH": "GHS",
  "en-KE": "KES",
  "en-UG": "UGX",
  "en-TZ": "TZS",
  "en-ZM": "ZMW",
  "en-ZW": "ZWL",
  "en-BW": "BWP",
  "en-NAM": "NAD",
  "en-MY": "MYR",
  "en-SG": "SGD",
  "en-HK": "HKD",
  "en-IN": "INR",
  "en-PK": "PKR",
  "en-BD": "BDT",
  "en-LK": "LKR",
  "en-NP": "NPR",
  "en-JM": "JMD",
  "en-TT": "TTD",
  "en-BZ": "BZD",
  "en-BS": "BSD",
  "en-BB": "BBD",
  "en-GY": "GYD",
  "en-AE": "AED",
  "en-SA": "SAR",
  "en-KW": "KWD",
  "en-QA": "QAR",
  "en-OM": "OMR",
  "en-BH": "BHD",
  "en-JO": "JOD",
  "en-LB": "LBP",
  "de-DE": "EUR",
  "de-AT": "EUR",
  "de-CH": "CHF",
  "de-LU": "EUR",
  "de-LI": "CHF",
  "es-ES": "EUR",
  "es-MX": "MXN",
  "es-AR": "ARS",
  "es-CO": "COP",
  "es-CL": "CLP",
  "es-PE": "PEN",
  "es-VE": "VES",
  "es-EC": "USD",
  "es-BO": "BOB",
  "es-PY": "PYG",
  "es-UY": "UYU",
  "es-CU": "CUP",
  "es-DO": "DOP",
  "es-PR": "USD",
  "es-GT": "GTQ",
  "es-HN": "HNL",
  "es-SV": "USD",
  "es-NI": "NIO",
  "es-CR": "CRC",
  "es-PA": "PAB",
  "pt-BR": "BRL",
  "pt-PT": "EUR",
  "pt-AO": "AOA",
  "pt-MZ": "MZN",
  "pt-CV": "CVE",
  "pt-GW": "XOF",
  "pt-ST": "STN",
  "it-IT": "EUR",
  "it-CH": "CHF",
  "nl-NL": "EUR",
  "nl-BE": "EUR",
  "nl-SR": "SRD",
  "nl-AW": "AWG",
  "nl-CW": "ANG",
  "sv-SE": "SEK",
  "da-DK": "DKK",
  "nb-NO": "NOK",
  "nn-NO": "NOK",
  "fi-FI": "EUR",
  "is-IS": "ISK",
  "pl-PL": "PLN",
  "cs-CZ": "CZK",
  "sk-SK": "EUR",
  "hu-HU": "HUF",
  "ro-RO": "RON",
  "bg-BG": "BGN",
  "hr-HR": "EUR",
  "sl-SI": "EUR",
  "et-EE": "EUR",
  "lv-LV": "EUR",
  "lt-LT": "EUR",
  "el-GR": "EUR",
  "tr-TR": "TRY",
  "ru-RU": "RUB",
  "uk-UA": "UAH",
  "ar-SA": "SAR",
  "ar-AE": "AED",
  "ar-EG": "EGP",
  "ar-MA": "MAD",
  "ar-DZ": "DZD",
  "ar-TN": "TND",
  "ar-KW": "KWD",
  "ar-QA": "QAR",
  "ar-OM": "OMR",
  "ar-BH": "BHD",
  "ar-JO": "JOD",
  "ar-LB": "LBP",
  "ar-IQ": "IQD",
  "ar-SD": "SDG",
  "ar-SS": "SSP",
  "ar-SO": "SOS",
  "ar-DJ": "DJF",
  "ar-KM": "KMF",
  "ar-MR": "MRU",
  "he-IL": "ILS",
  "hi-IN": "INR",
  "bn-IN": "INR",
  "bn-BD": "BDT",
  "ta-IN": "INR",
  "ta-LK": "LKR",
  "te-IN": "INR",
  "mr-IN": "INR",
  "ur-PK": "PKR",
  "ur-IN": "INR",
  "th-TH": "THB",
  "vi-VN": "VND",
  "id-ID": "IDR",
  "ms-MY": "MYR",
  "ms-SG": "SGD",
  "ms-BN": "BND",
  "ja-JP": "JPY",
  "ko-KR": "KRW",
  "zh-CN": "CNY",
  "zh-HK": "HKD",
  "zh-TW": "TWD",
  "zh-SG": "SGD",
  "zh-MO": "MOP",
};

// Country code (from ipapi.co) to currency fallback
const COUNTRY_TO_CURRENCY = {
  TD: "XAF",
  CM: "XAF",
  CF: "XAF",
  GQ: "XAF",
  GA: "XAF",
  CG: "XAF",
  SN: "XOF",
  CI: "XOF",
  ML: "XOF",
  BF: "XOF",
  NE: "XOF",
  TG: "XOF",
  BJ: "XOF",
  GN: "GNF",
  GW: "XOF",
  CD: "CDF",
  DJ: "DJF",
  KM: "KMF",
  MG: "MGA",
  MU: "MUR",
  RW: "RWF",
  NG: "NGN",
  GH: "GHS",
  KE: "KES",
  UG: "UGX",
  TZ: "TZS",
  ZM: "ZMW",
  ZW: "ZWL",
  BW: "BWP",
  NA: "NAD",
  ZA: "ZAR",
  LS: "LSL",
  SZ: "SZL",
  AO: "AOA",
  MZ: "MZN",
  CV: "CVE",
  ST: "STN",
  EG: "EGP",
  SD: "SDG",
  SS: "SSP",
  SO: "SOS",
  MR: "MRU",
  MA: "MAD",
  DZ: "DZD",
  TN: "TND",
  LY: "LYD",
  ET: "ETB",
  ER: "ERN",
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  AU: "AUD",
  NZ: "NZD",
  IE: "EUR",
  FR: "EUR",
  DE: "EUR",
  ES: "EUR",
  IT: "EUR",
  PT: "EUR",
  BE: "EUR",
  NL: "EUR",
  AT: "EUR",
  LU: "EUR",
  FI: "EUR",
  GR: "EUR",
  SI: "EUR",
  SK: "EUR",
  EE: "EUR",
  LV: "EUR",
  LT: "EUR",
  MT: "EUR",
  CY: "EUR",
  HR: "EUR",
  ME: "EUR",
  RS: "RSD",
  MK: "MKD",
  BA: "BAM",
  AL: "ALL",
  BG: "BGN",
  RO: "RON",
  PL: "PLN",
  CZ: "CZK",
  HU: "HUF",
  DK: "DKK",
  SE: "SEK",
  NO: "NOK",
  IS: "ISK",
  CH: "CHF",
  LI: "CHF",
  MC: "EUR",
  AD: "EUR",
  SM: "EUR",
  VA: "EUR",
  RU: "RUB",
  BY: "BYN",
  UA: "UAH",
  MD: "MDL",
  GE: "GEL",
  AM: "AMD",
  AZ: "AZN",
  KZ: "KZT",
  UZ: "UZS",
  KG: "KGS",
  TJ: "TJS",
  TM: "TMT",
  MN: "MNT",
  CN: "CNY",
  JP: "JPY",
  KR: "KRW",
  IN: "INR",
  PK: "PKR",
  BD: "BDT",
  LK: "LKR",
  NP: "NPR",
  MM: "MMK",
  TH: "THB",
  VN: "VND",
  MY: "MYR",
  SG: "SGD",
  ID: "IDR",
  PH: "PHP",
  BN: "BND",
  KH: "KHR",
  LA: "LAK",
  HK: "HKD",
  TW: "TWD",
  MO: "MOP",
  MX: "MXN",
  GT: "GTQ",
  BZ: "BZD",
  HN: "HNL",
  SV: "USD",
  NI: "NIO",
  CR: "CRC",
  PA: "PAB",
  CO: "COP",
  VE: "VES",
  EC: "USD",
  PE: "PEN",
  BO: "BOB",
  PY: "PYG",
  CL: "CLP",
  AR: "ARS",
  UY: "UYU",
  BR: "BRL",
  GY: "GYD",
  SR: "SRD",
  FK: "FKP",
  CU: "CUP",
  DO: "DOP",
  HT: "HTG",
  JM: "JMD",
  TT: "TTD",
  BB: "BBD",
  BS: "BSD",
  AG: "XCD",
  DM: "XCD",
  KN: "XCD",
  LC: "XCD",
  VC: "XCD",
  GD: "XCD",
  AI: "XCD",
  MQ: "EUR",
  GP: "EUR",
  RE: "EUR",
  YT: "EUR",
  GF: "EUR",
  PF: "XPF",
  NC: "XPF",
  WF: "XPF",
  PM: "EUR",
  BL: "EUR",
  MF: "EUR",
  SX: "ANG",
  CW: "ANG",
  AW: "AWG",
  KY: "KYD",
  BM: "BMD",
  TC: "USD",
  VG: "USD",
  VI: "USD",
  PR: "USD",
  GU: "USD",
  AS: "USD",
  MP: "USD",
  MH: "USD",
  FM: "USD",
  PW: "USD",
  KI: "AUD",
  NR: "AUD",
  TV: "AUD",
  CK: "NZD",
  NU: "NZD",
  TK: "NZD",
  WS: "WST",
  TO: "TOP",
  FJ: "FJD",
  PG: "PGK",
  SB: "SBD",
  VU: "VUV",
  AF: "AFN",
  IR: "IRR",
  IQ: "IQD",
  IL: "ILS",
  PS: "ILS",
  JO: "JOD",
  LB: "LBP",
  SY: "SYP",
  YE: "YER",
  OM: "OMR",
  AE: "AED",
  QA: "QAR",
  BH: "BHD",
  KW: "KWD",
  SA: "SAR",
  TR: "TRY",
};

const LOCALE_BY_CURRENCY = {
  XOF: "fr-FR",
  XAF: "fr-FR",
  EUR: "fr-FR",
  GBP: "en-GB",
  USD: "en-US",
  CAD: "en-CA",
  NGN: "en-NG",
  MAD: "fr-MA",
  DZD: "fr-DZ",
  TND: "fr-TN",
  EGP: "ar-EG",
  ZAR: "en-ZA",
  GHS: "en-GH",
  KES: "en-KE",
  RWF: "fr-RW",
  CDF: "fr-CD",
  CNY: "zh-CN",
  INR: "en-IN",
  AED: "ar-AE",
  SAR: "ar-SA",
  RUB: "ru-RU",
  BRL: "pt-BR",
  JPY: "ja-JP",
  KRW: "ko-KR",
  AUD: "en-AU",
  CHF: "de-CH",
  SEK: "sv-SE",
  NOK: "nb-NO",
  DKK: "da-DK",
  PLN: "pl-PL",
  TRY: "tr-TR",
};

function getCached(isSuperAdmin) {
  try {
    const raw = localStorage.getItem(cacheKey(isSuperAdmin));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp < CACHE_TTL) return parsed;
  } catch {}
  return null;
}

function setCache(isSuperAdmin, data) {
  try {
    localStorage.setItem(cacheKey(isSuperAdmin), JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {}
}

function getCurrencyFromLocale(locale) {
  if (!locale) return null;
  const exact = LOCALE_TO_CURRENCY[locale];
  if (exact) return exact;
  const base = locale.split("-")[0];
  // Try any locale with same language code
  const match = Object.keys(LOCALE_TO_CURRENCY).find((l) => l.startsWith(base + "-"));
  if (match) return LOCALE_TO_CURRENCY[match];
  return null;
}

function getCurrencyFromCountry(countryCode) {
  if (!countryCode) return null;
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || null;
}

function getLocaleForCurrency(currency) {
  return LOCALE_BY_CURRENCY[currency] || "fr-FR";
}

function getSymbol(currency, locale) {
  // CFA franc zones (Central XAF / West XOF) are conventionally written "FCFA".
  if (currency === "XAF" || currency === "XOF") return "FCFA";
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const parts = fmt.formatToParts(0);
    const currencyPart = parts.find((p) => p.type === "currency");
    if (currencyPart) return currencyPart.value;
  } catch {}
  return currency;
}

async function fetchWithTimeout(url, timeout = 5000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(id);
    if (!res.ok) throw new Error("HTTP " + res.status);
    return await res.json();
  } catch (e) {
    clearTimeout(id);
    throw e;
  }
}

async function fetchRates(base) {
  try {
    return await fetchWithTimeout(`https://api.exchangerate-api.com/v4/latest/${base}`, 6000);
  } catch {
    try {
      return await fetchWithTimeout(`https://open.er-api.com/v6/latest/${base}`, 6000);
    } catch {
      return null;
    }
  }
}

async function detectCurrency(isSuperAdmin) {
  // Super-admin accounting is always displayed in XAF/FCFA at the fixed EUR peg.
  if (isSuperAdmin) {
    const result = {
      currency: "XAF",
      symbol: "FCFA",
      locale: "fr-FR",
      // Subscription prices are already in FCFA; only usage costs (EUR base)
      // are converted via eurRate.
      rate: 1,
      eurRate: EUR_TO_XAF,
      country: "TD",
      rates: {},
      eurRates: { XAF: EUR_TO_XAF },
    };
    setCache(isSuperAdmin, result);
    return result;
  }

  const cached = getCached(isSuperAdmin);
  if (cached) return cached;

  // First, estimate from browser locale for fast initial display
  const browserLocale = typeof navigator !== "undefined" ? navigator.language || navigator.userLanguage || "fr-FR" : "fr-FR";
  const localeCurrency = getCurrencyFromLocale(browserLocale);

  let detectedCurrency = localeCurrency || "XOF";
  let detectedCountry = "TD";
  let ipDetected = false;

  // Primary: server-side detection from the real client IP (authoritative).
  try {
    const geo = await fetchWithTimeout(`${API_BASE}/api/geo-currency`);
    const country = geo.country;
    const ipCurrency = geo.currency || getCurrencyFromCountry(country);
    if (ipCurrency) {
      detectedCurrency = ipCurrency;
      detectedCountry = country || detectedCountry;
      ipDetected = true;
    }
  } catch (e) {
    console.warn("[Currency] server geo detection failed", e.message);
  }

  // Fallback: third-party IP geolocation if the server could not resolve
  if (!ipDetected) {
    try {
      const geo = await fetchWithTimeout("https://ipapi.co/json/");
      const country = geo.country_code || geo.country;
      const ipCurrency = geo.currency || getCurrencyFromCountry(country);
      if (ipCurrency) {
        detectedCurrency = ipCurrency;
        detectedCountry = country || detectedCountry;
      }
    } catch (e) {
      console.warn("[Currency] IP detection failed, using locale", e.message);
    }
  }

  // Fetch exchange rates from EUR (base accounting currency) and XOF (legacy/local)
  let rate = 1;
  let rates = {};
  let eurRate = 1;
  let eurRates = {};

  try {
    const ratesData = await fetchRates("XOF");
    rates = ratesData?.rates || {};
    rate = rates[detectedCurrency] || 1;
  } catch (e) {
    console.warn("[Currency] XOF exchange rate fetch failed", e.message);
  }

  try {
    const eurRatesData = await fetchRates("EUR");
    eurRates = eurRatesData?.rates || {};
    eurRate = eurRates[detectedCurrency] || 1;
  } catch (e) {
    console.warn("[Currency] EUR exchange rate fetch failed", e.message);
    // Fallback: derive EUR rate from XOF rate using the BEAC peg.
    if (rates[detectedCurrency]) {
      eurRate = rates[detectedCurrency] * EUR_TO_XAF;
    } else if (detectedCurrency === "XAF" || detectedCurrency === "XOF") {
      eurRate = EUR_TO_XAF;
    }
  }

  const locale = getLocaleForCurrency(detectedCurrency);
  const symbol = getSymbol(detectedCurrency, locale);

  const result = {
    currency: detectedCurrency,
    symbol,
    locale,
    rate,        // Rate from XOF to detectedCurrency (legacy, kept for compatibility)
    country: detectedCountry,
    rates,       // All rates from XOF
    eurRate,     // Rate from EUR (accounting base) to detectedCurrency
    eurRates,    // All rates from EUR
  };

  setCache(isSuperAdmin, result);
  return result;
}

export function useCurrency() {
  const { isSuperAdmin } = useApp();
  const [info, setInfo] = useState(getFallback(isSuperAdmin));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    detectCurrency(isSuperAdmin).then((data) => {
      if (!cancelled) setInfo(data);
    }).catch(() => {
      if (!cancelled) setInfo(getFallback(isSuperAdmin));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const formatPrice = useCallback(
    (amountXOF) => {
      if (amountXOF === null || amountXOF === undefined) return { amount: "", symbol: info.symbol, raw: 0 };
      const converted = Math.round(amountXOF * info.rate);
      const formatted = new Intl.NumberFormat(info.locale, {
        maximumFractionDigits: 0,
      }).format(converted);
      return { amount: `${formatted} ${info.symbol}`, symbol: info.symbol, raw: converted };
    },
    [info]
  );

  // Convert amounts expressed in the accounting base currency (EUR) to the
  // user's detected currency. For super-admins this is always XAF/FCFA.
  const formatPriceUSD = useCallback(
    (amountEUR) => {
      if (amountEUR === null || amountEUR === undefined) return { amount: "", symbol: info.symbol, raw: 0 };
      const converted = Math.round(amountEUR * info.eurRate);
      const formatted = new Intl.NumberFormat(info.locale, {
        maximumFractionDigits: 0,
      }).format(converted);
      return { amount: `${formatted} ${info.symbol}`, symbol: info.symbol, raw: converted };
    },
    [info]
  );

  const formatNumber = useCallback(
    (amountXOF) => {
      if (amountXOF === null || amountXOF === undefined) return "";
      const converted = Math.round(amountXOF * info.rate);
      return new Intl.NumberFormat(info.locale, {
        maximumFractionDigits: 0,
      }).format(converted);
    },
    [info]
  );

  const formatNumberUSD = useCallback(
    (amountEUR) => {
      if (amountEUR === null || amountEUR === undefined) return "";
      const converted = Math.round(amountEUR * info.eurRate);
      return new Intl.NumberFormat(info.locale, {
        maximumFractionDigits: 0,
      }).format(converted);
    },
    [info]
  );

  return { ...info, loading, formatPrice, formatPriceUSD, formatNumber, formatNumberUSD };
}
